import { messages } from "../constants/actionTypes";

const defaultState = {
  error: "",
  // detail: why the open conversation could not be loaded, with the request to replay (GET_MESSAGE_DETAIL_FAIL).
  // markRead, markUnread, archive, unArchive: why a row action failed, as { message, status } (MESSAGE_ACTION_FAIL). A
  // later success of the same action clears its own entry; the page shows all but markRead.
  errors: { sendMessage: null, list: null, loadMore: null, detail: null, markRead: null, markUnread: null, archive: null, unArchive: null },
  // Replies that were sent and failed, by listing_message_id. failureId rises with every failure, so the page can tell
  // a second failure of the same conversation from the one it has already told the member about.
  failedSends: {},
  failureSeq: 0,
  // Unsent reply text by listing_message_id (as a string), kept while the member looks at another conversation. In
  // memory only: it goes with a reload. Written by SET_DRAFT, dropped when that conversation's reply is delivered.
  drafts: {},
  // The last reply delivered, for whichever conversation it was sent from. sendMessageSuccessId only moves for the open
  // conversation; this one also tells the reply box that the text it still holds was sent while the member was away.
  deliveredReply: { conversationId: null, seq: 0 },
  sendMessageSuccessId: 0,
  preloaders: {
    messages: false,
    loadMore: false,
    readMessage: false,
    messageDetail: false,
    sendMessage: false
  },
  messages: [],
  // The team the rows in `messages` were loaded for ("" for the member's own inbox), written by GET_MESSAGES. The page
  // opens nothing from them while it is in another one, and a list for another team drops the selection (GROWTH-7324).
  listTeamId: "",
  messageDetail: {},
  messageDetailMessages: [],
  selectedConversationId: null,
  messagesStateCount: 0,
  // Mirrors the server's `pagination` block. `page` is the highest page fetched into `messages` (the list only
  // grows forward). `shifted` counts the rows archived or un-archived out of the loaded list since the last
  // fetch: each one moves every later row up a position, so the next offset page would skip a conversation.
  pagination: { page: 1, perPage: 0, totalPages: 0, totalRecords: 0, shifted: 0 },
  // Counts first-page loads (GET_MESSAGES only). MessagesList scrolls its column back to the top when it changes:
  // left deep in a shorter list, the sentinel would be in view and pull in page after page unasked.
  listVersion: 0,
  activeListings: [],
  isActiveFilterModal: false,
  success: {
    unread: false,
    archive: false,
    unArchive: false
  },
  activityBlocks: {
    conversation: false,
    detail: false,
    detailDesktop: true,
    messages: true
  }
};

// Rows removed from the loaded list move every later row up on the server, so remember how many.
const shiftPagination = (state, nextList) => {
  const removed = state.messages.length - nextList.length;

  return removed ? { ...state.pagination, shifted: state.pagination.shifted + removed } : state.pagination;
};

export default function(state = defaultState, action) {
  const { type, payload } = action;
  let updatedMessagesList;
  switch (type) {
    case messages.SET_ACTIVE_BLOCKS:
      return {
        ...state,
        activityBlocks: { ...state.activityBlocks, ...payload }
      };

    case messages.DISABLE_SUCCESS:
      return {
        ...state,
        success: defaultState.success
      };

    case messages.TOGGLE_LOADING:
      return {
        ...state,
        preloaders: { ...state.preloaders, [payload.section]: payload.isLoading },
        error: ""
      };

    case messages.GET_MESSAGE_DETAIL_LOADING:
      return {
        ...state,
        preloaders: { ...state.preloaders, messageDetail: true },
        messageDetail: {},
        messageDetailMessages: [],
        // A new request supersedes the failure of the previous one: the error belongs to the conversation being opened.
        errors: { ...state.errors, detail: null },
        selectedConversationId: payload?.listingId ?? state.selectedConversationId
      };

    case messages.GET_MESSAGE_DETAIL_FAIL:
      // The detail request has no abort, so a failure can arrive after the member opened another conversation. It is
      // that conversation's pane now; leave it alone, and leave the preloader to the request that still owns it.
      if (String(payload.listingId) !== String(state.selectedConversationId)) {
        return state;
      }

      return {
        ...state,
        preloaders: { ...state.preloaders, messageDetail: false },
        errors: {
          ...state.errors,
          detail: {
            listingId: payload.listingId,
            message: payload.message,
            status: payload.status,
            details: payload.details,
            request: payload.request
          }
        }
      };

    case messages.GET_MESSAGES_FAIL:
      return {
        ...state,
        // Whichever request failed, neither is still in flight; payload.section records which one it was.
        preloaders: { ...state.preloaders, messages: false, loadMore: false },
        errors: {
          ...state.errors,
          [payload.section]: {
            message: payload.message,
            status: payload.status,
            details: payload.details
          }
        }
      };

    case messages.GET_MESSAGES: {
      // Keep the open thread across a list refresh: blanking it unconditionally made the page fetch the
      // same thread again after every reload. Clear it only when the selected conversation is not in the
      // list the server just returned (a team switch, or a filter that excludes it), so a thread from
      // another context cannot stay on screen. selectedConversationId is written by
      // GET_MESSAGE_DETAIL_LOADING and is left alone here within a context: a list response can land between that and
      // GET_MESSAGE_DETAIL, and SEND_MESSAGE relies on it to find the open thread. A list for another team clears it (below).
      const isSelectedStillListed =
        state.selectedConversationId !== null &&
        payload.messages.some(item => String(item.listing_message_id) === String(state.selectedConversationId));
      // A list for another team is a different context: the selection that it does not hold is that context's, so it goes
      // (and the spinner of its thread with it), and a detail answer for it that is still in flight lands on no one
      // (GROWTH-7324). Within the same context the selection stays, for a deep link or a filter that left it out.
      const listTeamId = String(payload.teamId ?? "");
      const isOtherContext = listTeamId !== state.listTeamId;
      const dropsSelection = isOtherContext && !isSelectedStillListed;

      return {
        ...state,
        messages: payload.messages,
        listTeamId,
        selectedConversationId: dropsSelection ? null : state.selectedConversationId,
        activeListings: payload.activeListings,
        // A first-page load restarts pagination; the pages accumulated so far go with the rows they held.
        pagination: payload.pagination ? { ...payload.pagination, shifted: 0 } : defaultState.pagination,
        listVersion: state.listVersion + 1,
        preloaders: { ...state.preloaders, messages: false, loadMore: false, ...(dropsSelection ? { messageDetail: false } : {}) },
        // The failure belongs to the thread on screen: when the list drops that thread (a team switch, a filter), it goes too.
        errors: { ...state.errors, list: null, loadMore: null, detail: isSelectedStillListed ? state.errors.detail : null },
        messageDetail: isSelectedStillListed ? state.messageDetail : {},
        messageDetailMessages: isSelectedStillListed ? state.messageDetailMessages : [],
        messagesStateCount: payload.messages.length
      };
    }

    case messages.APPEND_MESSAGES: {
      // The list is ordered by q.sent, so a reply that moves a conversation can return it on a later page while
      // it is already on screen. Drop it on email_conversation_id, the id READ/UNREAD/ARCHIVE_MESSAGE key on.
      const listedIds = new Set(state.messages.map(item => String(item.email_conversation_id)));
      const newRows = payload.messages.filter(item => !listedIds.has(String(item.email_conversation_id)));
      // Same array when the page added nothing: the page's list-update effect keys off its identity.
      const nextMessages = newRows.length ? [...state.messages, ...newRows] : state.messages;

      // Deliberately leaves the open thread, activeListings and errors.list alone: loading older conversations
      // must not blank the thread being read.
      return {
        ...state,
        messages: nextMessages,
        pagination: { ...payload.pagination, shifted: 0 },
        preloaders: { ...state.preloaders, loadMore: false },
        errors: { ...state.errors, loadMore: null },
        messagesStateCount: nextMessages.length
      };
    }

    case messages.GET_MESSAGE_DETAIL:
      // The detail request has no abort, so a response can arrive after the member opened another conversation. It is
      // that conversation's pane now, and the reply box sends to whatever thread is in it: leave it alone, and leave the
      // preloader and errors.detail to the request that still owns them. listingId is the one the thunk asked for, so
      // the check does not depend on the shape of the server's answer; an action without one is applied as before.
      if (payload.listingId != null && String(payload.listingId) !== String(state.selectedConversationId)) {
        return state;
      }

      return {
        ...state,
        preloaders: { ...state.preloaders, messageDetail: false },
        errors: { ...state.errors, detail: null },
        messageDetail: payload.messageDetail,
        messageDetailMessages: payload.messages
      };

    case messages.READ_MESSAGE:
      updatedMessagesList = state.messages.map(item => {
        if (String(item.email_conversation_id) === String(payload)) {
          return { ...item, is_read: "1" };
        }
        return item;
      });

      return {
        ...state,
        messages: updatedMessagesList,
        errors: { ...state.errors, markRead: null }
      };

    // A failed row action (read, unread, archive, un-archive). Only the error is stored: the row, the selection and
    // the success flags stay as they were, and the thunk turns its own preloader off.
    case messages.MESSAGE_ACTION_FAIL:
      return {
        ...state,
        errors: { ...state.errors, [payload.section]: { message: payload.message, status: payload.status } }
      };

    case messages.CLEAR_MESSAGE_ACTION_ERRORS:
      return {
        ...state,
        errors: { ...state.errors, markRead: null, markUnread: null, archive: null, unArchive: null }
      };

    case messages.UNREAD_MESSAGE:
      updatedMessagesList = state.messages.map(item => {
        if (String(item.email_conversation_id) === String(payload)) {
          return { ...item, is_read: "0" };
        }
        return item;
      });

      return {
        ...state,
        messages: updatedMessagesList,
        errors: { ...state.errors, markUnread: null },
        success: {
          ...state.success,
          unread: true
        },
        preloaders: {
          ...state.preloaders,
          unreadMessage: false
        }
      };

    case messages.ARCHIVE_MESSAGE: {
      const emailConversationId = payload?.emailConversationId ?? payload;
      const listingMessageId = payload?.listingMessageId;
      updatedMessagesList = state.messages.filter(item => String(item.email_conversation_id) !== String(emailConversationId));
      const clearsOpenThread = listingMessageId != null && String(state.selectedConversationId) === String(listingMessageId);

      return {
        ...state,
        messages: updatedMessagesList,
        pagination: shiftPagination(state, updatedMessagesList),
        errors: { ...state.errors, archive: null },
        success: {
          ...state.success,
          archive: true
        },
        ...(clearsOpenThread ? { messageDetail: {}, messageDetailMessages: [], selectedConversationId: null } : {})
      };
    }

    case messages.UN_ARCHIVE_MESSAGE:
      updatedMessagesList = state.messages.filter(item => String(item.email_conversation_id) !== String(payload));

      return {
        ...state,
        messages: updatedMessagesList,
        pagination: shiftPagination(state, updatedMessagesList),
        errors: { ...state.errors, unArchive: null },
        success: {
          ...state.success,
          unArchive: true
        }
      };

    case messages.SEND_MESSAGE: {
      const nextFailedSends = { ...state.failedSends };
      delete nextFailedSends[payload.listing_message_id];

      // A delivered reply is no longer a draft. Dropped for whichever conversation sent it, like failedSends: the
      // member may have moved on before it resolved, and the text must not come back as an unsent draft.
      const nextDrafts = { ...state.drafts };
      delete nextDrafts[String(payload.listing_message_id)];

      // Only touch the open thread's UI, keyed on selectedConversationId, which survives the
      // messageDetail blank during a detail (re)fetch (messageDetail.listing_message_id would go
      // undefined and wrongly skip the composer clear). If the member switched conversations before
      // the reply resolved, the append + composer clear (success-edge counter) must target the
      // conversation still open, not the one that happened to send. failedSends stays keyed + unconditional.
      const isOpenConversation = String(payload.listing_message_id) === String(state.selectedConversationId);

      // An idempotent replay returns the row that was already dispatched; if it is already in the
      // thread (double-click, or a retry after the append succeeded) do not append it twice.
      const alreadyInThread =
        payload.message?.email_conversation_id != null &&
        state.messageDetailMessages.some(item => String(item.email_conversation_id) === String(payload.message.email_conversation_id));
      const shouldAppend = isOpenConversation && !alreadyInThread;

      return {
        ...state,
        preloaders: { ...state.preloaders, sendMessage: false },
        messageDetailMessages: shouldAppend ? [...state.messageDetailMessages, payload.message] : state.messageDetailMessages,
        sendMessageSuccessId: isOpenConversation ? (state.sendMessageSuccessId || 0) + 1 : state.sendMessageSuccessId,
        errors: { ...state.errors, sendMessage: null },
        failedSends: nextFailedSends,
        drafts: nextDrafts,
        deliveredReply: { conversationId: String(payload.listing_message_id), seq: state.deliveredReply.seq + 1 }
      };
    }

    case messages.SEND_MESSAGE_FAIL:
      return {
        ...state,
        preloaders: { ...state.preloaders, sendMessage: false },
        errors: {
          ...state.errors,
          [payload.section]: {
            message: payload.message,
            status: payload.status,
            details: payload.details
          }
        },
        failureSeq: state.failureSeq + 1,
        failedSends: {
          ...state.failedSends,
          [payload.request?.listing_message_id]: {
            messageData: payload.request,
            errorMessage: payload.message,
            failureId: state.failureSeq + 1
          }
        }
      };

    case messages.CLEAR_SEND_FAILURE: {
      const nextFailedSends = { ...state.failedSends };
      delete nextFailedSends[payload];

      return {
        ...state,
        errors: { ...state.errors, sendMessage: null },
        failedSends: nextFailedSends
      };
    }

    case messages.SET_DRAFT: {
      const { listingMessageId, text } = payload;

      // A reply box that was never opened in a conversation has no id to keep its text under.
      if (listingMessageId === null || listingMessageId === undefined || listingMessageId === "") {
        return state;
      }

      const draftKey = String(listingMessageId);

      // Same state back when nothing changes, so the page does not re-render for it.
      if (text ? state.drafts[draftKey] === text : !(draftKey in state.drafts)) {
        return state;
      }

      const nextDrafts = { ...state.drafts };
      if (text) {
        nextDrafts[draftKey] = text;
      } else {
        delete nextDrafts[draftKey];
      }

      return {
        ...state,
        drafts: nextDrafts
      };
    }

    case messages.SET_ACTIVITY_FILTER:
      return {
        ...state,
        isActiveFilterModal: payload
      };

    default:
      return state;
  }
}
